import type { StationData } from '../../api/stationObservationsService';
import type { DashboardWidgetConfig } from './widgets/types';
import { getWidget } from './widgets/widgetRegistry';

export interface ResolvedStationReading {
  observation: StationData['observations'][0];
  readingConfig: StationData['reading_config'];
  stationReference: string;
}

/** Average readings by field name across multiple observations. */
export function averageObservations(
  observations: StationData['observations'],
  stations: StationData[],
  readingConfig: StationData['reading_config'],
): { observation: StationData['observations'][0]; readingConfig: StationData['reading_config'] } {
  const seen = new Set(readingConfig.map((c) => c.name));
  const mergedConfig = [...readingConfig];
  for (const station of stations) {
    for (const cfg of station.reading_config) {
      if (!seen.has(cfg.name)) {
        seen.add(cfg.name);
        mergedConfig.push(cfg);
      }
    }
  }

  const avgReadings: (number | null)[] = new Array(mergedConfig.length).fill(null);

  for (let i = 0; i < mergedConfig.length; i++) {
    const fieldName = mergedConfig[i].name;
    let sum = 0;
    let count = 0;
    for (let j = 0; j < observations.length; j++) {
      const station = stations[j];
      const obs = observations[j];
      const paramIndex = station.reading_config.findIndex((config) => config.name === fieldName);
      if (paramIndex !== -1) {
        const val = obs.readings[paramIndex];
        if (val !== null && val !== undefined) {
          sum += val;
          count++;
        }
      }
    }
    avgReadings[i] = count > 0 ? sum / count : null;
  }

  const latestObs = observations.reduce((latest, obs) =>
    new Date(obs.reading_timestamp).getTime() > new Date(latest.reading_timestamp).getTime()
      ? obs
      : latest,
  );

  return {
    observation: {
      reading_timestamp: latestObs.reading_timestamp,
      car_wind_direction: null,
      readings: avgReadings,
    },
    readingConfig: mergedConfig,
  };
}

function findObsAtOrBefore(
  observations: StationData['observations'],
  targetTime: number,
): StationData['observations'][0] | null {
  let best: StationData['observations'][0] | null = null;
  let bestTime = -Infinity;

  for (const obs of observations) {
    const t = new Date(obs.reading_timestamp).getTime();
    if (t <= targetTime && t > bestTime) {
      best = obs;
      bestTime = t;
    }
  }
  return best;
}

/** Resolve the closest observation at or before targetTime for a station or all-stations average. */
export function resolveStationReadingAtTime(
  stations: StationData[],
  targetTime: number,
  stationMode: string,
): ResolvedStationReading | null {
  if (stations.length === 0) return null;

  if (stationMode === 'avg') {
    const closestPerStation: StationData['observations'] = [];
    const stationsWithData: StationData[] = [];
    let refConfig: StationData['reading_config'] | null = null;

    for (const station of stations) {
      if (station.observations.length === 0) continue;
      if (!refConfig) refConfig = station.reading_config;
      const closest = findObsAtOrBefore(station.observations, targetTime);
      if (closest) {
        closestPerStation.push(closest);
        stationsWithData.push(station);
      }
    }

    if (closestPerStation.length === 0 || !refConfig) return null;
    const { observation, readingConfig } = averageObservations(
      closestPerStation,
      stationsWithData,
      refConfig,
    );
    return { observation, readingConfig, stationReference: 'avg' };
  }

  const station =
    stations.find((s) => s.station_reference === stationMode) ??
    stations.find((s) => s.observations.length > 0);
  if (!station || station.observations.length === 0) return null;

  const observation = findObsAtOrBefore(station.observations, targetTime);
  if (!observation) return null;

  return {
    observation,
    readingConfig: station.reading_config,
    stationReference: station.station_reference,
  };
}

export function getReadingValue(
  resolved: ResolvedStationReading,
  fieldName: string,
): number | null {
  const index = resolved.readingConfig.findIndex((cfg) => cfg.name === fieldName);
  return index >= 0 ? resolved.observation.readings[index] : null;
}

export function widgetNeedsStationObservations(
  type: string,
  config: Record<string, unknown>,
): boolean {
  const def = getWidget(type);
  if (!def?.capabilities.requiresObservations) return false;
  if (type === 'radar-map') {
    return config.showWindArrows === true;
  }
  return true;
}

export function dashboardNeedsStationObservations(
  widgets: DashboardWidgetConfig[],
): boolean {
  return widgets.some((w) => widgetNeedsStationObservations(w.type, w.config));
}
